import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { API, SOCIAL_LINKS } from "@/lib/constants";
import {
  Mail, ArrowRight, ArrowLeft, CheckCircle, Facebook, Instagram, Linkedin,
  Link2, MessageCircle
} from "lucide-react";

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(res.data.message);
      setSubscribed(true);
      setEmail('');
    } catch (e) {
      toast.error('Errore nell\'iscrizione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="flex items-center justify-center gap-3 bg-[#F7F0E1] border border-gold/30 rounded-lg px-6 py-4 max-w-md mx-auto" data-testid="newsletter-success">
        <CheckCircle size={20} className="text-gold" />
        <p className="text-gold font-medium">Iscrizione completata! Riceverai i prossimi articoli.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="La tua email..."
        className="flex-1 bg-white border border-[#D6CFC0] focus:border-gold rounded-lg px-5 py-4 text-[#0D1527] outline-none placeholder:text-[#98A0B0]"
        data-testid="newsletter-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded-lg font-bold tracking-wide transition-all hover:scale-105 disabled:opacity-50 whitespace-nowrap"
        data-testid="newsletter-submit"
      >
        {loading ? 'Invio...' : 'ISCRIVITI'}
      </button>
    </form>
  );
};

// Markdown renderer styled for the dark/gold theme
const isMarkdownContent = (content) =>
  /^#{1,6}\s/m.test(content) || content.includes('**') || /^\|.*\|$/m.test(content);

const mdComponents = {
  h1: ({ children }) => <h2 className="font-heading text-2xl md:text-3xl text-[#0D1527] mt-12 mb-5 border-l-4 border-gold pl-4">{children}</h2>,
  h2: ({ children }) => <h2 className="font-heading text-2xl md:text-3xl text-[#0D1527] mt-12 mb-5 border-l-4 border-gold pl-4">{children}</h2>,
  h3: ({ children }) => <h3 className="font-heading text-xl text-[#0D1527] mt-9 mb-3">{children}</h3>,
  p: ({ children }) => <p className="text-[#4A5568] leading-[1.8] mb-5">{children}</p>,
  strong: ({ children }) => <strong className="text-[#0D1527] font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-[#4A5568] italic">{children}</em>,
  a: ({ href, children }) => <a href={href} className="text-gold underline underline-offset-4 hover:text-yellow-400" target="_blank" rel="noopener noreferrer">{children}</a>,
  ul: ({ children }) => <ul className="list-disc pl-6 my-6 space-y-2.5 marker:text-gold">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 my-6 space-y-2.5 marker:text-gold marker:font-bold">{children}</ol>,
  li: ({ children }) => <li className="text-[#4A5568] leading-relaxed pl-1">{children}</li>,
  hr: () => <hr className="border-[#E8E2D5] my-10" />,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-gold/50 pl-6 italic text-[#3B4658] my-6">{children}</blockquote>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-8 border border-[#E8E2D5] rounded-lg">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#F7F0E1]">{children}</thead>,
  th: ({ children }) => <th className="text-left text-gold font-semibold px-4 py-3 border-b border-[#E8E2D5] whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-4 py-2.5 text-[#4A5568] border-b border-[#EFE9DE] align-top">{children}</td>,
  code: ({ children }) => <code className="bg-[#EDE6DA] text-gold px-1.5 py-0.5 rounded text-[0.9em]">{children}</code>,
};

// Article body: markdown when detected, legacy heuristic renderer otherwise
export const ArticleBody = ({ content }) => (
  <div className="prose prose-invert max-w-none article-content">
    {isMarkdownContent(content) ? (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {content}
      </ReactMarkdown>
    ) : content.split('\n\n').map((paragraph, i) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return null;

      // Helper: render inline emphasis
      const renderText = (text) => {
        // Bold text between quotes or key phrases
        const parts = text.split(/(".*?")/g);
        return parts.map((part, idx) => {
          if (part.startsWith('"') && part.endsWith('"')) {
            return <strong key={idx} className="text-[#0D1527] font-semibold">{part}</strong>;
          }
          return <span key={idx}>{part}</span>;
        });
      };

      // Pure bullet list
      const isList = trimmed.split('\n').every(line => line.trim().startsWith('- ') || line.trim() === '');
      if (isList) {
        return (
          <ul key={i} className="space-y-3 my-6 pl-2">
            {trimmed.split('\n').filter(l => l.trim().startsWith('- ')).map((item, j) => (
              <li key={j} className="flex items-start gap-3 text-[#4A5568]">
                <CheckCircle size={16} className="text-gold flex-shrink-0 mt-1.5" />
                <span className="leading-relaxed">{renderText(item.replace(/^- /, ''))}</span>
              </li>
            ))}
          </ul>
        );
      }

      // Numbered list (1. 2. 3. etc)
      const isNumberedList = trimmed.split('\n').every(line => /^\d+[\.\)]\s/.test(line.trim()) || line.trim() === '');
      if (isNumberedList) {
        return (
          <ol key={i} className="space-y-4 my-6 pl-2">
            {trimmed.split('\n').filter(l => /^\d+[\.\)]\s/.test(l.trim())).map((item, j) => {
              const text = item.replace(/^\d+[\.\)]\s*/, '');
              return (
                <li key={j} className="flex items-start gap-4 text-[#4A5568]">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#F7F0E1] text-gold rounded-full flex items-center justify-center font-bold text-sm">{j + 1}</span>
                  <span className="leading-relaxed pt-1">{renderText(text)}</span>
                </li>
              );
            })}
          </ol>
        );
      }

      // Heading detection: short line, starts with uppercase, all caps or ends with : or ?
      if (trimmed.length < 100 && !trimmed.includes('.')) {
        const isHeading = /^[A-Z]/.test(trimmed) && (
          trimmed.toUpperCase() === trimmed ||
          trimmed.endsWith(':') ||
          trimmed.endsWith('?') ||
          /^\d+\)/.test(trimmed)
        );
        if (isHeading) {
          return <h3 key={i} className="font-heading text-2xl text-[#0D1527] mt-10 mb-4 border-l-4 border-gold pl-4">{trimmed}</h3>;
        }
      }

      // Mixed content: paragraphs with inline lists
      const lines = trimmed.split('\n');
      if (lines.length > 1 && lines.some(l => l.trim().startsWith('- '))) {
        return (
          <div key={i} className="my-5">
            {lines.map((line, j) => {
              const lt = line.trim();
              if (!lt) return null;
              if (lt.startsWith('- ')) {
                return (
                  <div key={j} className="flex items-start gap-3 text-[#4A5568] mb-2.5 pl-2">
                    <CheckCircle size={16} className="text-gold flex-shrink-0 mt-1.5" />
                    <span className="leading-relaxed">{renderText(lt.replace(/^- /, ''))}</span>
                  </div>
                );
              }
              // Sub-heading inside a paragraph
              if (lt.length < 80 && lt.endsWith(':')) {
                return <p key={j} className="text-[#0D1527] font-semibold mt-4 mb-2">{lt}</p>;
              }
              return <p key={j} className="text-[#4A5568] leading-relaxed mb-2">{renderText(lt)}</p>;
            })}
          </div>
        );
      }

      // Regular paragraph with emphasis
      return <p key={i} className="text-[#4A5568] leading-[1.8] mb-5">{renderText(trimmed)}</p>;
    })}
  </div>
);

// Blog Page (list)
const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setArticles(res.data);
      } catch (e) {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-[#F4EFEA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </main>
    );
  }

  return (
    <main className="pt-20" data-testid="blog-page">
      <PageSEO {...SEO.approfondimenti} />
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-gold font-bold tracking-widest text-sm mb-4">APPROFONDIMENTI</p>
            <h1 className="font-heading text-6xl md:text-7xl text-[#0D1527] leading-none mb-6">
              APPROFONDIMENTI<br /><span className="text-gold">E STRATEGIE</span>
            </h1>
            <p className="text-xl text-[#4A5568] max-w-2xl mx-auto">
              Articoli pratici per imprenditori che vogliono capire i numeri 
              e prendere decisioni migliori.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => {
              const imgSrc = article.image_url || article.image;
              const dateStr = article.published_at
                ? new Date(article.published_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
                : article.date || '';
              return (
                <Link
                  key={article.id}
                  to={article.external_url || `/approfondimenti/${article.slug || article.id}`}
                  reloadDocument={!!article.external_url}
                  className="group bg-white border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/50 transition-all cursor-pointer block"
                  data-testid={`blog-article-${article.id}`}
                >
                  {imgSrc && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={imgSrc} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-xs font-bold text-gold bg-[#F7F0E1] px-3 py-1 rounded">{article.category}</span>
                      <span className="text-xs text-[#64708A]">{dateStr}</span>
                    </div>
                    <h2 className="font-heading text-2xl text-[#0D1527] mb-3 group-hover:text-gold transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-[#4A5568] text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-gold text-sm font-medium group-hover:gap-3 transition-all">
                      Leggi l'articolo <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter + Social */}
      <section className="py-24 bg-[#F4EFEA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-[#F7F0E1] text-gold px-4 py-2 rounded-full text-sm font-bold tracking-wider mb-6">
            <Mail size={16} />
            NEWSLETTER
          </div>
          <h2 className="font-heading text-4xl text-[#0D1527] mb-4">RICEVI I PROSSIMI ARTICOLI VIA EMAIL</h2>
          <p className="text-[#4A5568] mb-8 max-w-xl mx-auto">Lascia la tua email e riceverai automaticamente ogni nuovo approfondimento direttamente nella tua casella di posta.</p>
          
          <NewsletterForm />
          
          <div className="mt-12 pt-8 border-t border-[#E8E2D5]">
            <p className="text-[#8B94A6] text-sm mb-4">Seguici anche sui social</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { href: SOCIAL_LINKS.facebook, icon: Facebook, label: "Facebook" },
                { href: SOCIAL_LINKS.instagram, icon: Instagram, label: "Instagram" },
                { href: SOCIAL_LINKS.linkedin, icon: Linkedin, label: "LinkedIn" }
              ].map((s) => (
                <a 
                  key={s.label}
                  href={s.href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 bg-[#EDE6DA] hover:bg-gold hover:text-black px-6 py-3 rounded font-medium transition-all"
                >
                  <s.icon size={20} />
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Dedicated article page: /approfondimenti/:slug
const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        if (r.data.external_url) {
          window.location.replace(r.data.external_url);
          return;
        }
        if (r.data.html_page) {
          // Articolo HTML standalone: sostituisce l'intero documento
          fetch(`${API}/blog/page/${r.data.slug}`)
            .then(res => res.text())
            .then(htmlDoc => {
              document.open();
              document.write(htmlDoc);
              document.close();
            });
          return;
        }
        setArticle(r.data);
      })
      .catch(() => navigate('/approfondimenti', { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (!article) return;
    axios.get(`${API}/blog`).then(r => {
      const others = r.data.filter(p => p.id !== article.id);
      const same = others.filter(p => p.category === article.category);
      const rest = others.filter(p => p.category !== article.category);
      setRelated([...same, ...rest].slice(0, 2));
    }).catch(() => setRelated([]));
  }, [article]);

  if (loading || !article) {
    return (
      <main className="pt-20 min-h-screen bg-[#F4EFEA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </main>
    );
  }

  const dateStr = article.published_at
    ? new Date(article.published_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.casalisilvia.com';
  const cleanUrl = `${origin}/approfondimenti/${article.slug || article.id}`;
  const shareUrl = `${origin}/api/blog/share/${article.slug || article.id}`;
  const absImg = article.image_url
    ? (article.image_url.startsWith('/') ? `${origin}${article.image_url}` : article.image_url)
    : null;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success('Link copiato negli appunti'),
      () => toast.error('Impossibile copiare il link')
    );
  };

  return (
    <main className="pt-20 min-h-screen bg-[#F4EFEA]" data-testid="article-page">
      <PageSEO
        title={`${article.title} | SC Dottori Commercialisti`}
        description={article.excerpt}
        ogType="article"
        ogImage={absImg || undefined}
      />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: absImg || undefined,
        datePublished: article.published_at,
        author: { "@type": "Person", "@id": "https://casalisilvia.com/#silvia-casali", name: article.author || "Silvia Casali" },
        publisher: { "@id": "https://casalisilvia.com/#organization" },
        mainEntityOfPage: cleanUrl
      }) }} />

      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <Link
          to="/approfondimenti"
          className="inline-flex items-center gap-2 text-[#64708A] hover:text-gold text-sm font-medium tracking-wide transition-colors mb-8"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={16} /> TUTTI GLI APPROFONDIMENTI
        </Link>

        {article.image_url && (
          <div className="aspect-video rounded-lg overflow-hidden mb-10 border border-[#E8E2D5]">
            <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 mb-5">
          <span className="text-xs font-bold text-gold bg-[#F7F0E1] px-3 py-1 rounded">{article.category}</span>
          <span className="text-xs text-[#64708A]">{dateStr}</span>
          {article.author && <span className="text-xs text-[#8B94A6]">{article.author}</span>}
        </div>

        <h1 className="font-heading text-4xl md:text-5xl text-[#0D1527] mb-6 leading-tight">
          {article.title}
        </h1>

        <p className="text-xl text-[#3B4658] mb-6 leading-relaxed italic border-l-4 border-gold/50 pl-6">
          {article.excerpt}
        </p>

        {/* Share bar */}
        <div className="flex flex-wrap items-center gap-3 mb-10 pb-8 border-b border-[#E8E2D5]">
          <span className="text-[#8B94A6] text-xs tracking-widest font-bold mr-1">CONDIVIDI</span>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white border border-[#E8E2D5] hover:border-gold hover:text-gold text-[#4A5568] px-4 py-2 rounded text-sm transition-colors"
            data-testid="share-linkedin"
          >
            <Linkedin size={15} /> LinkedIn
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white border border-[#E8E2D5] hover:border-gold hover:text-gold text-[#4A5568] px-4 py-2 rounded text-sm transition-colors"
            data-testid="share-facebook"
          >
            <Facebook size={15} /> Facebook
          </a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + shareUrl)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white border border-[#E8E2D5] hover:border-gold hover:text-gold text-[#4A5568] px-4 py-2 rounded text-sm transition-colors"
            data-testid="share-whatsapp"
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
          <button
            onClick={copyLink}
            className="flex items-center gap-2 bg-white border border-[#E8E2D5] hover:border-gold hover:text-gold text-[#4A5568] px-4 py-2 rounded text-sm transition-colors"
            data-testid="share-copy-link"
          >
            <Link2 size={15} /> Copia link
          </button>
        </div>

        <ArticleBody content={article.content} />

        <div className="mt-12 pt-8 border-t border-[#E8E2D5]">
          <p className="text-[#64708A] mb-4">Vuoi approfondire questo argomento per la tua azienda?</p>
          <Link 
            to="/contatti" 
            className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105"
            data-testid="article-cta-contatti"
          >
            PARLIAMONE <ArrowRight size={18} />
          </Link>
        </div>
      </article>

      {/* Articoli correlati */}
      {related.length > 0 && (
        <section className="py-16 bg-[#F4EFEA] border-t border-[#E8E2D5]" data-testid="related-articles">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-gold font-bold tracking-widest text-xs mb-6">CONTINUA A LEGGERE</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map(rel => (
                <Link
                  key={rel.id}
                  to={rel.external_url || `/approfondimenti/${rel.slug || rel.id}`}
                  reloadDocument={!!rel.external_url}
                  className="group bg-white border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/50 transition-all block"
                  data-testid={`related-article-${rel.id}`}
                >
                  {rel.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img src={rel.image_url} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <span className="text-xs font-bold text-gold bg-[#F7F0E1] px-2.5 py-1 rounded">{rel.category}</span>
                    <h3 className="font-heading text-xl text-[#0D1527] mt-3 mb-2 group-hover:text-gold transition-colors">{rel.title}</h3>
                    <p className="text-[#4A5568] text-sm line-clamp-2">{rel.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-20 bg-[#F4EFEA] border-t border-[#EFE9DE]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl text-[#0D1527] mb-3">RICEVI I PROSSIMI ARTICOLI VIA EMAIL</h2>
          <p className="text-[#4A5568] mb-8 max-w-xl mx-auto">Ogni nuovo approfondimento direttamente nella tua casella di posta.</p>
          <NewsletterForm />
        </div>
      </section>
    </main>
  );
};

export { BlogPage, ArticlePage };
